/**
 * BestTimes.jsx
 * When this brand's posts have actually done best.
 *
 * Two shapes from one component, because they are the same finding shown at
 * different sizes: a full panel on Analytics, and a strip of buttons under the
 * scheduling field on Create. The strip is the one that earns its keep — a
 * recommendation you have to remember and type in yourself is a recommendation
 * you will ignore.
 *
 * The confidence label is not decoration. Below a handful of measured posts
 * this shows general starting points rather than your figures, and says so, so
 * nobody schedules a month around eleven data points.
 */

import { useEffect, useState } from 'react';
import { api } from '../services/api.js';
import { Clock, Chart } from './Icons.jsx';

const CONFIDENCE_LABEL = {
  none: { text: 'Not your figures yet', tone: 'muted' },
  low: { text: 'Early days', tone: 'warn' },
  fair: { text: 'Reasonable evidence', tone: 'ok' },
  good: { text: 'Solid evidence', tone: 'ok' },
};

/**
 * @param {object} props
 * @param {string} props.brandId
 * @param {'panel'|'strip'} [props.variant]
 * @param {string|null} [props.platform] - narrow to one platform's audience
 * @param {(value: string) => void} [props.onPick] - given "2026-09-15T17:00"
 */
export default function BestTimes({ brandId, variant = 'panel', platform = null, onPick }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [view, setView] = useState(platform ?? 'overall');

  useEffect(() => {
    // An empty brandId is meaningful on Analytics — it means every brand — but
    // never on the composer, where a suggestion has to be about the brand you
    // are actually writing for.
    if (!brandId && variant === 'strip') {
      setData(null);
      return;
    }
    let stopped = false;
    api.analytics
      .bestTimes({ brandId })
      .then((d) => !stopped && (setData(d), setError(null)))
      .catch((e) => !stopped && setError(e));
    return () => {
      stopped = true;
    };
  }, [brandId, variant]);

  if (!brandId && variant === 'strip') return null;

  if (error) {
    return variant === 'strip' ? null : (
      <div className="card">
        <h2>
          <Clock size={17} /> Best times to post
        </h2>
        <p className="hint">Couldn’t work these out: {error.message}</p>
      </div>
    );
  }

  if (!data) {
    return variant === 'strip' ? null : (
      <div className="card">
        <h2>
          <Clock size={17} /> Best times to post
        </h2>
        <p className="hint">Working it out…</p>
      </div>
    );
  }

  const result = view === 'overall' ? data.overall : (data.byPlatform?.[view] ?? data.overall);
  const badge = CONFIDENCE_LABEL[result.confidence] ?? CONFIDENCE_LABEL.none;

  // ---------- the compact form, under a scheduling field ----------
  if (variant === 'strip') {
    return (
      <div className="besttimes-strip">
        <span className="besttimes-strip__label">
          <Clock size={14} />
          {result.fallback ? 'Common good times' : 'Your best times'}
        </span>

        {result.best.slice(0, 4).map((slot) => (
          <button
            key={`${slot.weekday}-${slot.hour}`}
            type="button"
            className="chip-btn chip-btn--time"
            onClick={() => slot.next && onPick?.(slot.next)}
            disabled={!slot.next}
            title={
              result.fallback
                ? 'A general starting point — not enough of your own posts have been measured yet'
                : `${slot.posts} post${slot.posts === 1 ? '' : 's'} at this time, ` +
                  `${slot.lift >= 0 ? '+' : ''}${slot.lift}% against this brand’s average`
            }
          >
            {slot.label}
            {!result.fallback && slot.lift > 0 && (
              <span className="chip__lift">+{slot.lift}%</span>
            )}
          </button>
        ))}

        {result.fallback && (
          <span className="besttimes-strip__note">general, not yours yet</span>
        )}
      </div>
    );
  }

  // ---------- the full panel ----------
  return (
    <div className="card">
      <h2>
        <Clock size={17} /> Best times to post
        <span className={`pill pill--${badge.tone}`}>{badge.text}</span>
      </h2>

      <p className="hint">{result.message}</p>

      <div className="btn-row" style={{ marginBottom: 14 }}>
        <button
          type="button"
          className={`btn btn--sm ${view === 'overall' ? 'btn--primary' : ''}`}
          onClick={() => setView('overall')}
        >
          Both platforms
        </button>
        {Object.keys(data.byPlatform ?? {}).map((key) => (
          <button
            key={key}
            type="button"
            className={`btn btn--sm ${view === key ? 'btn--primary' : ''}`}
            onClick={() => setView(key)}
          >
            {key[0].toUpperCase() + key.slice(1)}
          </button>
        ))}
      </div>

      <ol className="besttimes">
        {result.best.map((slot, i) => (
          <li key={`${slot.weekday}-${slot.hour}`} className="besttimes__row">
            <span className="besttimes__rank">{i + 1}</span>
            <span className="besttimes__when">
              <strong>{slot.day}</strong>
              <span className="besttimes__time">{slot.time}</span>
            </span>

            <span className="besttimes__evidence">
              {result.fallback ? (
                <span className="muted">starting point</span>
              ) : (
                <>
                  {slot.posts} post{slot.posts === 1 ? '' : 's'}
                  {slot.lift !== 0 && (
                    <span className={slot.lift > 0 ? 'delta delta--up' : 'delta delta--down'}>
                      {slot.lift > 0 ? '+' : ''}
                      {slot.lift}%
                    </span>
                  )}
                </>
              )}
            </span>

            {onPick && slot.next && (
              <button type="button" className="btn btn--sm" onClick={() => onPick(slot.next)}>
                Use this
              </button>
            )}
          </li>
        ))}
      </ol>

      {!result.fallback && result.byDay?.length > 0 && (
        <>
          <h3 className="besttimes__subhead">
            <Chart size={15} /> By day of the week
          </h3>
          <DayBars byDay={result.byDay} />
        </>
      )}

      <p className="hint" style={{ marginTop: 14 }}>
        Worked out from this brand’s own published posts and the figures Meta reported for them —
        measured as {result.measure === 'rate' ? 'engagement per person reached' : 'total engagement'}
        , in London time.
        {result.clipped > 0 &&
          ` ${result.clipped} unusually strong post${result.clipped === 1 ? ' was' : 's were'} capped so ` +
            'a single result cannot decide an hour on its own.'}
      </p>
    </div>
  );
}

/** A plain bar per weekday. Relative, because the absolute figure means little. */
function DayBars({ byDay }) {
  const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const top = Math.max(...byDay.map((d) => d.score), 0) || 1;
  const found = new Map(byDay.map((d) => [d.weekday, d]));

  return (
    <div className="daybars">
      {DAYS.map((name, i) => {
        const d = found.get(i);
        return (
          <div key={name} className="daybars__row">
            <span className="daybars__day">{name}</span>
            <span className="daybars__track">
              {d ? (
                <span className="daybars__fill" style={{ width: `${(d.score / top) * 100}%` }} />
              ) : null}
            </span>
            <span className="daybars__count">
              {d ? `${d.posts} post${d.posts === 1 ? '' : 's'}` : 'never posted'}
            </span>
          </div>
        );
      })}
    </div>
  );
}
